// Meta Learners Offers Database
// This is the single source of truth for all subscription offers

export const IN_STOCK_OFFER_IDS = new Set([
  "youtube-premium",
  "youtube-premium-yearly",
  "apple-one",
  "spotify-premium",
  "crunchyroll-mega",
  "netflix-premium",
  "crunchyroll-mega-yearly",
]);

export const isOfferInStock = (offerOrId) => {
  const offerId = typeof offerOrId === "string" ? offerOrId : offerOrId?.id;
  return IN_STOCK_OFFER_IDS.has(offerId);
};

export const offers = [
  {
    id: "youtube-premium",
    category: "Streaming",
    serviceName: "YouTube",
    planName: "Premium",
    originalPrice: 13.99,
    discountPercent: 60,
    finalPrice: 5.59,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Ad-free videos across all devices",
      "Background play & downloads",
      "YouTube Music Premium included",
      "Activation assistance",
      "Guided setup support",
      "24/7 customer support",
    ],
    deliveryNote: "Usually activated within 5-30 minutes",
    supportNote: "Full support from purchase to activation",
    badge: "Popular",
    logo: "🎥",
    details:
      "Enjoy YouTube without interruptions. Get ad-free viewing, background play, and offline downloads. Includes YouTube Music Premium at no extra cost. We guide you through the entire activation process.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "youtube-premium-yearly",
    category: "Streaming",
    serviceName: "YouTube",
    planName: "Premium Yearly",
    originalPrice: 139.99,
    discountPercent: 70,
    finalPrice: 41.99,
    billingPeriod: "/year",
    currencySymbol: "$",
    highlights: [
      "Ad-free videos across all devices",
      "Background play & downloads",
      "YouTube Music Premium included",
      "Activation assistance",
      "Guided setup support",
      "24/7 customer support",
    ],
    deliveryNote: "Usually activated within 5-30 minutes",
    supportNote: "Full support from purchase to activation",
    badge: "Popular",
    logo: "🎥",
    details:
      "Enjoy YouTube without interruptions. Get ad-free viewing, background play, and offline downloads. Includes YouTube Music Premium at no extra cost. We guide you through the entire activation process.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "crunchyroll-mega",
    category: "Streaming",
    serviceName: "Crunchyroll",
    planName: "Mega Fan",
    originalPrice: 11.99,
    discountPercent: 70,
    finalPrice: 3.59,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Ad-free anime streaming",
      "Simultaneous releases",
      "Offline viewing",
      "Fast activation",
      "Setup guidance included",
      "Dedicated support",
    ],
    deliveryNote: "Typically ready in 10-20 minutes",
    supportNote: "We assist you every step of the way",
    badge: "Best Value",
    logo: "📺",
    details:
      "Access the largest collection of anime. Watch the latest episodes as soon as they air in Japan. Download for offline viewing and enjoy an ad-free experience. Full activation support included.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "netflix-premium",
    category: "Streaming",
    serviceName: "Netflix",
    planName: "Premium 4K",
    originalPrice: 24.99,
    discountPercent: 60,
    finalPrice: 9.99,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Ultra HD 4K streaming",
      "Watch on 4 screens simultaneously",
      "Download on 6 devices",
      "Professional activation",
      "Complete setup guide",
      "Reliable support",
    ],
    deliveryNote: "Usually ready within 10-30 minutes",
    supportNote: "End-to-end activation assistance",
    badge: "Premium",
    logo: "🎬",
    details:
      "Watch unlimited movies and TV shows in stunning 4K Ultra HD. Support for up to 4 simultaneous screens. Download content on 6 devices. We provide full activation and setup support.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "spotify-premium",
    category: "Music",
    serviceName: "Spotify",
    planName: "Premium Individual",
    originalPrice: 11.99,
    discountPercent: 50,
    finalPrice: 5.99,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Ad-free music listening",
      "Offline downloads",
      "High-quality audio",
      "Quick activation",
      "Setup walkthrough",
      "Ongoing support",
    ],
    deliveryNote: "Ready in 5-15 minutes",
    supportNote: "Activation help and troubleshooting",
    badge: "Popular",
    logo: "🎵",
    details:
      "Stream over 100 million songs without ads. Download your favorite music for offline listening. Enjoy high-quality audio and unlimited skips. We ensure smooth activation.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "crunchyroll-mega-yearly",
    category: "Streaming",
    serviceName: "Crunchyroll",
    planName: "Mega Fan Yearly",
    originalPrice: 99.99,
    discountPercent: 70,
    finalPrice: 29.99,
    billingPeriod: "/year",
    currencySymbol: "$",
    highlights: [
      "Ad-free anime streaming",
      "Simultaneous releases",
      "Stream on a total of 4 devices at a time",
      "Access to Crunchyroll Game Vault",
      "Offline viewing",
      "Fast activation",
      "Setup guidance included",
      "Dedicated support",
    ],
    deliveryNote: "Typically ready in 10-20 minutes",
    supportNote: "We assist you every step of the way",
    badge: "Best Value",
    logo: "📺",
    details:
      "Access the largest collection of anime. Watch the latest episodes as soon as they air in Japan. Download for offline viewing and enjoy an ad-free experience. Full activation support included.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "apple-one",
    category: "Apple",
    serviceName: "Apple",
    planName: "One Individual",
    originalPrice: 19.95,
    discountPercent: 70,
    finalPrice: 5.99,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Apple Music, TV+, Arcade, iCloud+",
      "50GB cloud storage",
      "Expert activation help",
      "Complete setup assistance",
      "Priority support",
    ],
    deliveryNote: "Activation within 15-45 minutes",
    supportNote: "Full guidance for all Apple services",
    badge: "Premium Bundle",
    logo: "🍎",
    details:
      "Get the complete Apple ecosystem in one bundle. Includes Music, TV+, Arcade, News+, Fitness+, and 50GB of iCloud+ storage. We help activate and set up each service for you.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "disney-plus",
    category: "Streaming",
    serviceName: "Disney+",
    planName: "Premium",
    originalPrice: 10.99,
    discountPercent: 50,
    finalPrice: 5.49,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Disney, Pixar, Marvel, Star Wars",
      "4K UHD and Dolby Atmos",
      "Download for offline viewing",
      "Assisted activation",
      "Setup support included",
      "Responsive customer care",
    ],
    deliveryNote: "Activated in 10-25 minutes",
    supportNote: "We guide you through activation",
    badge: "Family Favorite",
    logo: "🏰",
    details:
      "Stream from Disney, Pixar, Marvel, Star Wars, and National Geographic. Enjoy content in 4K UHD with Dolby Atmos. Download shows for offline viewing. Full activation assistance provided.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "apple-music",
    category: "Music",
    serviceName: "Apple",
    planName: "Music Individual",
    originalPrice: 10.99,
    discountPercent: 60,
    finalPrice: 4.39,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Over 100 million songs",
      "Lossless and Spatial Audio",
      "Offline listening",
      "Fast setup",
      "Activation guidance",
      "Technical support",
    ],
    deliveryNote: "Ready within 5-20 minutes",
    supportNote: "Complete activation assistance",
    badge: "High Quality",
    logo: "🎧",
    details:
      "Access Apple's vast music library with lossless audio quality and Spatial Audio support. Download songs for offline listening. We help you activate and set up your account seamlessly.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "hbo-max",
    category: "Streaming",
    serviceName: "Max",
    planName: "Ad-Free",
    originalPrice: 15.99,
    discountPercent: 55,
    finalPrice: 7.19,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "HBO, Max Originals, Warner Bros",
      "4K UHD streaming",
      "Ad-free experience",
      "Professional activation",
      "Full setup support",
      "Dedicated help desk",
    ],
    deliveryNote: "Activated within 10-30 minutes",
    supportNote: "We ensure smooth activation",
    badge: "Premium",
    logo: "🎭",
    details:
      "Watch HBO originals, blockbuster movies, and exclusive Max content. Stream in 4K without ads. Download select titles for offline viewing. Full activation support included.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "paramount-plus",
    category: "Streaming",
    serviceName: "Paramount+",
    planName: "Premium",
    originalPrice: 11.99,
    discountPercent: 58,
    finalPrice: 5.03,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Live sports and news",
      "Movies and originals",
      "Download for offline",
      "Quick activation",
      "Setup walkthrough",
      "Customer support",
    ],
    deliveryNote: "Usually ready in 10-25 minutes",
    supportNote: "Activation and setup assistance",
    badge: "Sports & News",
    logo: "⛰️",
    details:
      "Stream live sports, breaking news, and thousands of episodes from CBS, MTV, and more. Watch Paramount+ Originals and blockbuster movies. We provide full activation help.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
  {
    id: "apple-arcade",
    category: "Apple",
    serviceName: "Apple",
    planName: "Arcade",
    originalPrice: 6.99,
    discountPercent: 65,
    finalPrice: 2.44,
    billingPeriod: "/mo",
    currencySymbol: "$",
    highlights: [
      "Over 200 premium games",
      "No ads or in-app purchases",
      "Play offline on all devices",
      "Fast activation",
      "Setup assistance",
      "Technical support",
    ],
    deliveryNote: "Activated in 5-15 minutes",
    supportNote: "We help with account setup",
    badge: "Gaming",
    logo: "🎮",
    details:
      "Play over 200 incredibly fun games with no ads or in-app purchases. Works across iPhone, iPad, Mac, and Apple TV. Download games for offline play. Full activation support included.",
    ctaInstagramUrl: "https://www.instagram.com/metalearners.store",
  },
];

// Helper function to get offers by category
export const getOffersByCategory = (category) => {
  if (category === "All") return offers;
  return offers.filter((offer) => offer.category === category);
};

// Helper function to get featured offers
export const getFeaturedOffers = () => {
  return offers
    .filter((offer) =>
      ["Popular", "Best Value", "Premium Bundle"].includes(offer.badge)
    )
    .slice(0, 3);
};

// Helper function to sort offers
export const sortOffers = (offersArray, sortBy) => {
  const sorted = [...offersArray];
  switch (sortBy) {
    case "discount":
      return sorted.sort((a, b) => b.discountPercent - a.discountPercent);
    case "price":
      return sorted.sort((a, b) => a.finalPrice - b.finalPrice);
    case "popular":
      // Featured badges first, then by discount
      return sorted.sort((a, b) => {
        const aFeatured = ["Popular", "Best Value", "Premium Bundle"].includes(
          a.badge
        );
        const bFeatured = ["Popular", "Best Value", "Premium Bundle"].includes(
          b.badge
        );
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;
        return b.discountPercent - a.discountPercent;
      });
    default:
      return sorted;
  }
};
